import React, { useState, useEffect, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { 
  XMarkIcon, 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  DocumentTextIcon,
  CalendarIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { Note, CreateNoteData, UpdateNoteData } from '../../types/note';
import { createNote, updateNote, deleteNote, getNotesBySiren } from '../../api/notesApi';
import { showSuccessToast } from '../../utils/toastUtils';

interface NotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  siren: string;
  companyName?: string;
}

const NotesModal: React.FC<NotesModalProps> = ({
  isOpen,
  onClose,
  siren,
  companyName
}) => {
  const [notes, setNotes] = useState<Note[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [editNoteContent, setEditNoteContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const newNoteTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Load notes when modal opens
  useEffect(() => {
    if (isOpen && siren) {
      loadNotes();
    }
  }, [isOpen, siren]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setNotes([]);
      setIsCreating(false);
      setEditingNote(null);
      setNewNoteContent('');
      setEditNoteContent('');
      setError(null);
    }
  }, [isOpen]);

  const loadNotes = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await getNotesBySiren([siren]);
      setNotes(response.data[siren] || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement des notes');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateNote = async () => {
    if (!newNoteContent.trim()) {
      setError('Le contenu de la note est requis');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const noteData: CreateNoteData = {
        siren,
        content: newNoteContent.trim()
      };

      const response = await createNote(noteData);
      setNotes(prev => [response.data, ...prev]);
      setNewNoteContent('');
      setIsCreating(false);
      showSuccessToast('Note créée avec succès', 'note-created');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la création de la note');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateNote = async (noteId: number) => {
    if (!editNoteContent.trim()) {
      setError('Le contenu de la note est requis');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const updateData: UpdateNoteData = {
        content: editNoteContent.trim()
      };

      const response = await updateNote(noteId, updateData);
      setNotes(prev => prev.map(note => 
        note.id === noteId ? response.data : note
      ));
      setEditingNote(null);
      setEditNoteContent('');
      showSuccessToast('Note mise à jour avec succès', 'note-updated');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la mise à jour de la note');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteNote = async (noteId: number) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette note ?')) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await deleteNote(noteId);
      setNotes(prev => prev.filter(note => note.id !== noteId));
      showSuccessToast('Note supprimée avec succès', 'note-deleted');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la suppression de la note');
    } finally {
      setIsLoading(false);
    }
  };

  const startEditing = (note: Note) => {
    setEditingNote(note);
    setEditNoteContent(note.content);
    setIsCreating(false);
  };

  const cancelEditing = () => {
    setEditingNote(null);
    setEditNoteContent('');
  };

  const startCreating = () => {
    setIsCreating(true);
    setEditingNote(null);
    setNewNoteContent('');
    // Focus the textarea after state update
    setTimeout(() => {
      newNoteTextareaRef.current?.focus();
    }, 0);
  };

  const cancelCreating = () => {
    setIsCreating(false);
    setNewNoteContent('');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[1001]" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900"
                    >
                      Notes - {companyName || 'Unité légale'}
                    </Dialog.Title>
                    <p className="text-sm text-gray-600 mt-1">
                      SIREN: {siren}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onClick={handleClose}
                    disabled={isLoading}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                {/* Header with Add button */}
                <div className="mb-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900">
                      Notes de l'unité légale
                    </h3>
                    
                    {!isCreating && (
                      <button
                        onClick={startCreating}
                        disabled={isLoading || editingNote !== null}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                      >
                        <PlusIcon className="h-4 w-4 mr-2" />
                        Ajouter une note
                      </button>
                    )}
                  </div>
                </div>

                {/* Create new note section */}
                {isCreating && (
                  <div className="mb-6 border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Nouvelle note</h4>
                    <p className="text-xs text-gray-500 mb-3">
                      Cette note sera associée à l'unité légale (SIREN: {siren})
                    </p>
                    <textarea
                      ref={newNoteTextareaRef}
                      value={newNoteContent}
                      onChange={(e) => setNewNoteContent(e.target.value)}
                      placeholder="Saisissez votre note..."
                      rows={4}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      disabled={isLoading}
                    />
                    <div className="flex justify-end space-x-3 mt-3">
                      <button
                        onClick={cancelCreating}
                        disabled={isLoading}
                        className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                      >
                        Annuler
                      </button>
                      <button
                        onClick={handleCreateNote}
                        disabled={isLoading || !newNoteContent.trim()}
                        className="px-3 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                      >
                        {isLoading ? 'Création...' : 'Créer'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Notes list */}
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {isLoading && notes.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="text-sm text-gray-500 mt-2">Chargement des notes...</p>
                    </div>
                  ) : notes.length === 0 ? (
                    <div className="text-center py-8">
                      <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-sm text-gray-500">
                        Aucune note pour cette unité légale
                      </p>
                    </div>
                  ) : (
                    notes.map((note) => (
                      <div key={note.id} className="border border-gray-200 rounded-lg p-4 bg-white">
                        {editingNote?.id === note.id ? (
                          // Edit mode
                          <div>
                            <textarea
                              value={editNoteContent}
                              onChange={(e) => setEditNoteContent(e.target.value)}
                              rows={4}
                              className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              disabled={isLoading}
                            />
                            <div className="flex justify-end space-x-3 mt-3">
                              <button
                                onClick={cancelEditing}
                                disabled={isLoading}
                                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                              >
                                Annuler
                              </button>
                              <button
                                onClick={() => handleUpdateNote(note.id)}
                                disabled={isLoading || !editNoteContent.trim()}
                                className="px-3 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                              >
                                {isLoading ? 'Mise à jour...' : 'Mettre à jour'}
                              </button>
                            </div>
                          </div>
                        ) : (
                          // View mode
                          <div>
                            <div className="flex justify-between items-start mb-3">
                              <div className="flex items-center space-x-2 text-xs text-gray-500">
                                <span className="flex items-center">
                                  <UserIcon className="h-3 w-3 mr-1" />
                                  {note.creator.firstName} {note.creator.lastName}
                                </span>
                                <span className="flex items-center">
                                  <CalendarIcon className="h-3 w-3 mr-1" />
                                  {formatDate(note.createdAt)}
                                </span>
                                {note.updater && (
                                  <span className="text-gray-400">
                                    (modifié le {formatDate(note.updatedAt)})
                                  </span>
                                )}
                              </div>
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => startEditing(note)}
                                  disabled={isLoading || isCreating}
                                  className="p-1 text-gray-400 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded disabled:opacity-50"
                                  title="Modifier"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteNote(note.id)}
                                  disabled={isLoading}
                                  className="p-1 text-gray-400 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 rounded disabled:opacity-50"
                                  title="Supprimer"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                            <div className="text-sm text-gray-900 whitespace-pre-wrap">
                              {note.content}
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>

                {/* Footer */}
                <div className="flex justify-end pt-6 border-t border-gray-200 mt-6">
                  <button
                    onClick={handleClose}
                    disabled={isLoading}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    Fermer
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default NotesModal; 